import { Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SelfV2 } from 'src/app/core/alcmeon-context/models/self-v2.model';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { Gender } from 'src/app/shared/enums';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { TimezoneService } from 'src/app/shared/services/timezone.service';

import { UserPreferencesService } from '../user-preferences.service';

@Component({
  selector: 'app-user-preferences-account',
  templateUrl: './user-preferences-account.component.html',
  styleUrls: ['../../user-preferences/shared/user-preferences.scss', './user-preferences-account.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserPreferencesAccountComponent implements OnDestroy {
  loggedUser: SelfV2 = this.alcmeonContextService.self;
  form = this.formBuilder.group({
    language: ['', Validators.required],
    realname: ['', Validators.required],
    timezone: ['', Validators.required],
    gender: [null],
  });
  timezoneItems = this.timezoneService.buildTimezoneDropdownItems(this.loggedUser.all_timezones) as Choice[];
  timezoneLabel = this.basicService.translate('preferences/timezone');
  genders: Choice[] = [];
  languages: Choice[] = [];

  subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private timezoneService: TimezoneService,
    private userPreferencesService: UserPreferencesService,
    private errorHandlingService: ErrorHandlingService,
    @Inject('$state') private $state: any,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('AppFactory') private appService: any // TODO create an AngularAppService
  ) {
    topToolbarService.setTitleTranslation('preferences/account', null);
    topToolbarService.setButton('save', this.save, null);

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() =>
        topToolbarService.updateButtonDisable(this.form.invalid || this.form.pristine, null)
      )
    );

    const { lang, timezone, realname, gender } = this.loggedUser;
    this.form.patchValue({ language: lang, timezone, realname, gender });
    this.languages = this.appService
      .getAvailableLanguages()
      .map((id: string) => ({ id, label: this.basicService.translate(id) }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

    this.genders = [
      { id: Gender.Unknown, label: this.basicService.translate('preferences/gender/unknown') },
      { id: Gender.Female, label: this.basicService.translate('preferences/gender/female') },
      { id: Gender.Male, label: this.basicService.translate('preferences/gender/male') },
      { id: Gender.NoChoice, label: this.basicService.translate('preferences/gender/no-choice') },
      { id: Gender.Neutral, label: this.basicService.translate('preferences/gender/neutral') },
    ];
  }

  private save = () => {
    this.basicService.showSpinner();
    const { language, timezone, realname, gender } = this.form.value;
    this.userPreferencesService
      .updateAccount({ language, timezone, realname, gender })
      .pipe(
        this.errorHandlingService.catchAlertAndThrowHttpError(),
        finalize(() => this.basicService.hideSpinner())
      )
      .subscribe({
        next: () => {
          const { language, timezone } = this.form.controls;
          if (language.dirty || timezone.dirty) {
            // The i18n static files are injected regarding the user languages. Let the state recompute correctly what it needs.
            this.$state.reload('auth.alcmeon');
          }

          this.basicService.toastSuccess('preferences/account/save_ok');
          this.form.markAsPristine();
          this.topToolbarService.updateButtonDisable(true, null);
        },
      });
  };

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.topToolbarService.clearButtons();
  }
}
